import React from 'react';
import { usePreviewContext } from '../providers/PreviewProvider';

export default function BoostDialog() {
  const {
    boostReport,
    setBoostReport,
    credits,
    resumeData,
    handleAIBoostConfirmed,
  } = usePreviewContext();

  if (boostReport !== 'confirm') return null;

  const hasName = !!resumeData?.name?.trim();
  const hasExperience = (resumeData?.experience?.length || 0) > 0 || (resumeData?.internships?.length || 0) > 0;
  const hasSkills = (resumeData?.skills?.length || 0) > 0;
  const hasProjects = (resumeData?.projects?.length || 0) > 0;

  const isEligible = hasName && hasExperience;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      {/* Inject premium micro-animations directly */}
      <style>{`
        @keyframes pulseGlow {
          0% { box-shadow: 0 0 15px rgba(249,115,22,0.4); }
          50% { box-shadow: 0 0 28px rgba(249,115,22,0.7); }
          100% { box-shadow: 0 0 15px rgba(249,115,22,0.4); }
        }
        @keyframes floatEffect {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
          100% { transform: translateY(0px); }
        }
      `}</style>

      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={() => setBoostReport(null)} />
      <div className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #09090b 0%, #1a0f05 100%)', border: '1px solid rgba(249,115,22,0.4)', boxShadow: '0 0 80px rgba(249,115,22,0.18), 0 25px 50px rgba(0,0,0,0.7)' }}>
        {/* Animated shimmer top border */}
        <div style={{ height: 2, background: 'linear-gradient(90deg, transparent, #F97316, #FB923C, #EA580C, transparent)', backgroundSize: '200% 100%', animation: 'bShimmer 2s linear infinite' }} />
        
        {/* Header */}
        <div className="px-6 pt-5 pb-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" 
            style={{ 
              background: 'linear-gradient(135deg, #F97316, #EA580C)', 
              boxShadow: '0 0 24px rgba(249,115,22,0.5)',
              animation: 'floatEffect 3s ease-in-out infinite'
            }}>
            <span className="text-2xl">✨</span>
          </div>
          <div>
            <h2 className="font-bold text-[17px] leading-tight tracking-tight" style={{ color: '#ffffff', margin: 0 }}>AI Resume Boost</h2>
            <p className="text-xs mt-1" style={{ color: '#a1a1aa', margin: 0 }}>Optimize layout and section contents with full ATS rules alignment</p>
          </div>
        </div>

        {/* Requirements Checklist */}
        <div className="px-6 pb-4 space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#71717a' }}>Requirement Checklist</p>
          
          {[
            { label: 'Full Name & Contact Info', met: hasName, desc: 'Required to anchor your profile identity' },
            { label: 'Work Experience or Internship', met: hasExperience, desc: 'Required to optimize professional achievements' },
            { label: 'Skills List', met: hasSkills, desc: 'Highly recommended for ATS keyword matching', optional: true },
            { label: 'Projects Section', met: hasProjects, desc: 'Recommended to showcase technical application', optional: true }
          ].map((req, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-xl transition-all"
              style={{
                background: req.met ? 'rgba(16,185,129,0.06)' : req.optional ? 'rgba(245,158,11,0.04)' : 'rgba(239,68,68,0.05)',
                border: `1px solid ${req.met ? 'rgba(16,185,129,0.25)' : req.optional ? 'rgba(245,158,11,0.18)' : 'rgba(239,68,68,0.25)'}`
              }}>
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] font-bold" style={{ color: '#f4f4f5' }}>
                  {req.label} {req.optional && <span className="text-[9px] font-normal ml-1" style={{ color: '#71717a' }}>(Optional)</span>}
                </span>
                <span className="text-[9px] leading-normal" style={{ color: '#a1a1aa' }}>{req.desc}</span>
              </div>
              <span className="text-[11px] font-bold shrink-0 ml-3"
                style={{ color: req.met ? '#34d399' : req.optional ? '#fbbf24' : '#f87171' }}>
                {req.met ? '✅ Ready' : req.optional ? '⚠️ Missing' : '❌ Missing'}
              </span>
            </div>
          ))}
        </div>

        {/* Eligibility Warning */}
        {!isEligible && (
          <div className="mx-6 mb-4 p-3 rounded-xl border text-[10px] leading-relaxed font-medium"
            style={{ 
              color: '#fca5a5', 
              background: 'rgba(239,68,68,0.06)', 
              borderColor: 'rgba(239,68,68,0.35)' 
            }}>
            ⚠️ <strong>Boost Blocked:</strong> Please add your name and at least one work experience or internship entry to unlock AI optimization.
          </div>
        )}

        {/* Cost */}
        <div className="mx-6 mb-4 px-4 py-2.5 rounded-xl flex items-center justify-between" 
          style={{ 
            background: 'rgba(245,158,11,0.06)', 
            border: '1px solid rgba(245,158,11,0.25)' 
          }}>
          <span className="text-xs font-semibold" style={{ color: '#fbbf24' }}>Cost</span>
          <span className="font-bold text-xs" style={{ color: '#fbbf24' }}>
            10 credits{credits !== null && credits !== undefined && credits !== "" && (
              <span className="font-normal text-[10px] ml-1" style={{ color: 'rgba(245,158,11,0.5)' }}>
                (you have {credits})
              </span>
            )}
          </span>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex gap-3">
          <button onClick={() => setBoostReport(null)}
            className="flex-1 py-3 rounded-xl text-sm font-medium transition-all hover:text-white"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#a1a1aa' }}>
            Cancel
          </button>
          <button 
            onClick={handleAIBoostConfirmed}
            disabled={!isEligible}
            className={`flex-1 py-3 rounded-xl text-sm font-bold text-white transition-all active:scale-[0.98] ${!isEligible ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-90'}`}
            style={{ 
              background: isEligible ? 'linear-gradient(135deg, #F97316, #EA580C)' : '#3f3f46',
              boxShadow: isEligible ? '0 0 24px rgba(249,115,22,0.4)' : 'none',
              animation: isEligible ? 'pulseGlow 2s infinite' : 'none'
            }}>
            Start Boost ✨
          </button>
        </div>
      </div>
    </div>
  );
}
